import type { ComponentPropsWithRef } from 'react';
import { forwardRef } from 'react';

import clsx from 'clsx';

const checkboxSize = {
    xs: 'w-2.5 h-2.5 border-[1.5px] box-border rounded-m',
    sm: 'w-3 h-3 border-[1.5px] box-border rounded-m',
    default: 'w-4 h-4 border-2 box-border rounded-m',
    large: 'w-5 h-5 border-2 box-border rounded-m'
};

const checkboxStyle = {
    default: clsx(
        'border-icoGray-300 dark:border-icoDarkMode-beton',
        'disabled:border-icoGray-300 disabled:bg-icoGray-300 dark:disabled:border-icoDarkMode-beton dark:disabled:bg-icoDarkMode-anthracite',
        'disabled:hover:border-icoGray-300 disabled:hover:bg-icoGray-300 dark:disabled:hover:border-icoDarkMode-beton dark:disabled:hover:bg-icoDarkMode-anthracite'
    ),
    gray400: clsx(
        'border-icoGray-400 dark:border-icoDarkMode-beton',
        'disabled:disabled:border-icoGray-400 disabled:bg-icoGray-300 dark:disabled:border-icoDarkMode-wolf dark:disabled:bg-icoDarkMode-beton',
        'disabled:hover:border-icoGray-400 disabled:hover:bg-icoGray-300 dark:disabled:hover:border-icoDarkMode-wolf dark:disabled:hover:bg-icoDarkMode-beton'
    ),
    white: clsx(
        'border-white',
        'disabled:border-icoGray-300 disabled:bg-icoGray-100 dark:disabled:border-icoDarkMode-beton dark:disabled:bg-icoDarkMode-anthracite',
        'disabled:hover:border-icoGray-300 disabled:hover:bg-icoGray-100 dark:disabled:hover:border-icoDarkMode-beton dark:disabled:hover:bg-icoDarkMode-anthracite'
    )
};

export type CheckboxProps = ComponentPropsWithRef<'input'> & {
    className?: string;
    disabled?: boolean;
    error?: boolean;
    boxSize?: keyof typeof checkboxSize;
    boxStyle?: keyof typeof checkboxStyle;
};

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
    (
        {
            className,
            disabled = false,
            error = false,
            boxSize = 'default',
            boxStyle = 'default',
            ...restProps
        },
        ref
    ) => {
        return (
            <input
                ref={ref}
                type="checkbox"
                className={clsx(
                    className,
                    checkboxSize[boxSize] ?? checkboxSize['default'],
                    'cursor-pointer disabled:cursor-not-allowed',
                    {
                        [checkboxStyle[boxStyle]]: !error,
                        'focus:ring-icoBlue-200 focus:ring-offset-0': !error,
                        'text-icoBlue hover:border-icoBlue focus:border-icoBlue dark:hover:border-icoBlue':
                            !error,
                        'checked:border-icoBlue': !error,
                        'border-icoRed text-icoRed focus:border-icoRed focus:ring-icoRed-200': error
                    }
                )}
                disabled={disabled}
                {...restProps}
            />
        );
    }
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
